import { Tooltip } from 'antd';
import { SUMMARY_DATA_BASIS_AREA_NOTE } from 'constants/uiConstants';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import React, { useMemo } from 'react';
import { type AreaSlice, computeAreaSlices } from './computeAreaSlices';
import type { AreaSummaryRow } from './computeSummaryReport';
import CopyButton from './CopyButton';
import { formatNumber } from './formatNumber';
import SectionCard from './SectionCard';
import { buildTsv } from './summary2Utils';

interface AreaSummaryTableProps {
  rows: AreaSummaryRow[];
}

/** Categorical slice colours in palette order — chart-1..6 then chart-9..12, skipping the Plan vs Actual pair (chart-7/8). Full class names so Tailwind keeps them. */
const SLICE_STROKE_ORDER = [
  'stroke-chart-1',
  'stroke-chart-2',
  'stroke-chart-3',
  'stroke-chart-4',
  'stroke-chart-5',
  'stroke-chart-6',
  'stroke-chart-9',
  'stroke-chart-10',
  'stroke-chart-11',
  'stroke-chart-12',
];
const SLICE_SWATCH_ORDER = [
  'bg-chart-1',
  'bg-chart-2',
  'bg-chart-3',
  'bg-chart-4',
  'bg-chart-5',
  'bg-chart-6',
  'bg-chart-9',
  'bg-chart-10',
  'bg-chart-11',
  'bg-chart-12',
];
// Past the palette, colours repeat as a lighter variant of the same hue.
const VARIANT_CLASS = 'opacity-50';

const sliceClass = (order: string[], slice: AreaSlice): string =>
  `${order[slice.paletteIndex % order.length]}${slice.isVariant ? ` ${VARIANT_CLASS}` : ''}`;

const PIE_SIZE = 100;
const PIE_CENTER = PIE_SIZE / 2;
const PIE_RADIUS = 34;
const PIE_STROKE = 20;
const CIRCUMFERENCE = 2 * Math.PI * PIE_RADIUS;

/** Crop area donut — plain SVG stroke-dasharray rings (not arc paths), one per crop, rotated to start at 12 o'clock. Every slice has a legend entry with crop, ha and %, so no crop is identified by colour alone. */
const AreaPieChart: React.FC<{ slices: AreaSlice[] }> = ({ slices }) => {
  if (slices.length === 0) return null;

  let cumulative = 0;

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-5">
      <svg
        viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`}
        className="h-64 w-64 shrink-0"
        role="img"
        aria-label="Crop area share"
      >
        {slices.map((slice) => {
          const dash = (slice.pct / 100) * CIRCUMFERENCE;
          const offset = -cumulative;
          cumulative += dash;
          if (dash <= 0) return null;
          return (
            <Tooltip
              key={slice.crop}
              title={`${slice.crop}: ${formatNumber(slice.areaHa)} ha (${formatNumber(slice.pct)}%)`}
            >
              <circle
                cx={PIE_CENTER}
                cy={PIE_CENTER}
                r={PIE_RADIUS}
                fill="none"
                strokeWidth={PIE_STROKE}
                strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                strokeDashoffset={offset}
                transform={`rotate(-90 ${PIE_CENTER} ${PIE_CENTER})`}
                className={`cursor-help transition-opacity hover:opacity-80 ${sliceClass(SLICE_STROKE_ORDER, slice)}`}
              />
            </Tooltip>
          );
        })}
      </svg>
      <ul className="flex flex-col gap-1.5">
        {slices.map((slice) => (
          <li
            key={slice.crop}
            className="flex items-center gap-1.5 text-sm text-charcoal"
          >
            <span
              className={`inline-block h-2.5 w-2.5 shrink-0 rounded-sm ${sliceClass(SLICE_SWATCH_ORDER, slice)}`}
            />
            <span className="truncate">{slice.crop}</span>
            <span className="tabular-nums text-gray-dark">
              {formatNumber(slice.areaHa)} ha · {formatNumber(slice.pct)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AreaSummaryTable: React.FC<AreaSummaryTableProps> = ({ rows }) => {
  const total = useMemo(
    () => rows.reduce((sum, row) => sum + row.areaHa, 0),
    [rows]
  );
  // % is a share of this scope's own total area, so it adds up to 100 at every scope.
  const slices = useMemo(() => computeAreaSlices(rows), [rows]);

  const { copied, copy } = useCopyToClipboard(() =>
    buildTsv(
      ['Crop', 'Area (ha)', '% of Total area'],
      [
        ...slices.map((slice) => [
          slice.crop,
          formatNumber(slice.areaHa),
          formatNumber(slice.pct),
        ]),
        ['Total', formatNumber(total), total > 0 ? formatNumber(100) : ''],
      ]
    )
  );

  if (rows.length === 0) return null;

  return (
    <SectionCard
      id="summary2-area"
      title="Crop area"
      subtitle={SUMMARY_DATA_BASIS_AREA_NOTE}
      actions={<CopyButton copied={copied} onCopy={copy} />}
    >
      <div className="flex flex-wrap items-start gap-6">
        <div className="summary-grid-scroll w-max max-w-full overflow-x-auto">
          <table className="w-max border-collapse text-base">
            <thead>
              <tr>
                <th className="max-w-[12rem] pb-2 pl-2 pr-6 text-left text-sm font-bold text-charcoal">
                  Crop
                </th>
                <th className="w-24 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                  Area (ha)
                </th>
                <th className="w-24 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                  % of Total area
                </th>
              </tr>
            </thead>
            <tbody>
              {slices.map((row, idx) => (
                <tr
                  key={row.crop}
                  className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <td className="max-w-[12rem] py-1.5 pl-2 pr-6 text-base text-charcoal">
                    {row.crop}
                  </td>
                  <td className="w-24 px-2 py-1.5 text-right tabular-nums text-charcoal">
                    {formatNumber(row.areaHa)}
                  </td>
                  <td className="w-24 px-2 py-1.5 text-right tabular-nums text-charcoal">
                    {formatNumber(row.pct)}%
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="py-1.5 pl-2 pr-6 text-right text-base font-bold text-charcoal">
                  Total:
                </td>
                <td className="w-24 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                  {formatNumber(total)}
                </td>
                <td className="w-24 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                  {formatNumber(100)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <AreaPieChart slices={slices} />
      </div>
    </SectionCard>
  );
};

export default AreaSummaryTable;
